// ---------------------------------------------------------------------------
// CustomPeriodPopover -- Date Range Picker for Custom Period
// ---------------------------------------------------------------------------
// Opens a popover with two calendars (From/To), quick presets, and
// Apply/Clear actions. Uses V11 theme tokens for dark/light support.
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useMemo } from 'react'
import { format, subMonths, differenceInCalendarDays } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import type { DateRange } from 'react-day-picker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomPeriodPopoverProps {
  readonly isActive: boolean
  readonly dateFrom: string | null
  readonly dateTo: string | null
  readonly onApply: (dateFrom: string, dateTo: string) => void
  readonly onClear: () => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDate(dateStr: string | null): Date | undefined {
  if (!dateStr) return undefined
  const d = new Date(dateStr + 'T12:00:00')
  return isNaN(d.getTime()) ? undefined : d
}

function formatDateStr(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function formatDisplayDate(date: Date): string {
  return format(date, 'MMM dd, yyyy')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CustomPeriodPopover({
  isActive,
  dateFrom,
  dateTo,
  onApply,
  onClear,
}: CustomPeriodPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Local selection state (only committed on Apply)
  const initialFrom = parseDate(dateFrom)
  const initialTo = parseDate(dateTo)
  const [range, setRange] = useState<DateRange | undefined>(
    initialFrom && initialTo ? { from: initialFrom, to: initialTo } : undefined,
  )

  // Sync local state when props change (e.g., URL navigation)
  const prevPropsRef = React.useRef({ dateFrom, dateTo })
  if (prevPropsRef.current.dateFrom !== dateFrom || prevPropsRef.current.dateTo !== dateTo) {
    prevPropsRef.current = { dateFrom, dateTo }
    const newFrom = parseDate(dateFrom)
    const newTo = parseDate(dateTo)
    if (newFrom && newTo) {
      setRange({ from: newFrom, to: newTo })
    }
  }

  // Default month for left calendar (show month of "from" or previous month)
  const defaultMonth = useMemo(() => {
    if (range?.from) return range.from
    return subMonths(new Date(), 1)
  }, [range?.from])

  // Pill display label
  const pillLabel = useMemo(() => {
    if (dateFrom && dateTo) {
      const from = parseDate(dateFrom)
      const to = parseDate(dateTo)
      if (from && to) {
        return `${format(from, 'MMM dd')} - ${format(to, 'MMM dd')}`
      }
    }
    return 'Custom'
  }, [dateFrom, dateTo])

  const handleApply = useCallback(() => {
    if (range?.from && range?.to) {
      onApply(formatDateStr(range.from), formatDateStr(range.to))
      setIsOpen(false)
    }
  }, [range, onApply])

  const handleClear = useCallback(() => {
    setRange(undefined)
    onClear()
    setIsOpen(false)
  }, [onClear])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    // Reset local state when opening if no committed dates
    if (open && !dateFrom && !dateTo) {
      setRange(undefined)
    }
  }, [dateFrom, dateTo])

  const canApply = range?.from && range?.to

  // Number of days in selected range (for info display)
  const dayCount = useMemo(() => {
    if (!range?.from || !range?.to) return null
    return differenceInCalendarDays(range.to, range.from) + 1
  }, [range?.from, range?.to])

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="time-pill-custom"
          type="button"
          aria-pressed={isActive}
          className={cn(
            'px-3 py-1.5 rounded-full text-xs font-medium transition-colors border',
            isActive
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-v11-surface border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover',
          )}
        >
          {pillLabel}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-auto p-0 bg-v11-surface border-v11-border shadow-xl rounded-xl"
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-v11-text-primary">
              Custom Date Range
            </h3>
            {dayCount !== null && (
              <span className="text-xs text-v11-text-muted">
                {dayCount} day{dayCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Date inputs display */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-v11-text-muted font-semibold mb-1">
                From
              </label>
              <div className="px-3 py-2 rounded-lg bg-v11-surface-hover border border-v11-border text-xs text-v11-text-primary">
                {range?.from ? formatDisplayDate(range.from) : 'Select start date'}
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-v11-text-muted font-semibold mb-1">
                To
              </label>
              <div className="px-3 py-2 rounded-lg bg-v11-surface-hover border border-v11-border text-xs text-v11-text-primary">
                {range?.to ? formatDisplayDate(range.to) : 'Select end date'}
              </div>
            </div>
          </div>

          {/* Dual Calendar */}
          <div className="flex justify-center">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              numberOfMonths={2}
              defaultMonth={defaultMonth}
              showOutsideDays
              classNames={{
                // Root & layout (v9 API)
                root: 'font-sans',
                months: 'relative flex gap-6',
                month: 'space-y-1',
                month_caption: 'flex justify-center items-center h-8',
                caption_label: 'text-xs font-semibold text-v11-text-primary',
                // Navigation
                nav: 'absolute top-0 left-0 right-0 flex items-center justify-between z-10 pointer-events-none',
                button_previous:
                  'h-7 w-7 bg-transparent p-0 text-v11-text-muted hover:text-v11-text-primary hover:bg-v11-surface-hover rounded transition-colors inline-flex items-center justify-center cursor-pointer border-0 pointer-events-auto',
                button_next:
                  'h-7 w-7 bg-transparent p-0 text-v11-text-muted hover:text-v11-text-primary hover:bg-v11-surface-hover rounded transition-colors inline-flex items-center justify-center cursor-pointer border-0 pointer-events-auto',
                chevron: 'h-4 w-4 fill-v11-text-muted',
                // Grid
                month_grid: 'border-collapse',
                weekdays: '',
                weekday:
                  'text-v11-text-muted font-medium text-[10px] w-8 pb-1 text-center',
                weeks: '',
                week: '',
                // Day cells (td) & buttons
                day: 'p-0 text-center align-middle h-8 w-8',
                day_button:
                  'inline-flex items-center justify-center h-7 w-7 p-0 text-xs font-normal text-v11-text-secondary hover:bg-v11-surface-hover hover:text-v11-text-primary rounded-full transition-colors cursor-pointer border-0 bg-transparent',
                // Selection states (applied to td)
                selected:
                  '[&>button]:bg-blue-500 [&>button]:text-white [&>button]:hover:bg-blue-600 [&>button]:hover:text-white',
                range_start:
                  'bg-gradient-to-r from-transparent from-50% to-blue-500/15 to-50% rounded-l-md',
                range_end:
                  'bg-gradient-to-l from-transparent from-50% to-blue-500/15 to-50% rounded-r-md',
                range_middle:
                  'bg-blue-500/15 [&>button]:text-blue-700 dark:[&>button]:text-blue-300 [&>button]:bg-transparent [&>button]:hover:bg-blue-500/25 [&>button]:hover:text-blue-800 dark:[&>button]:hover:text-blue-200',
                // Day flags
                today:
                  '[&>button]:ring-1 [&>button]:ring-blue-400 [&>button]:text-blue-400 [&>button]:font-semibold',
                outside: 'opacity-40',
                disabled: 'opacity-25 pointer-events-none',
                hidden: 'invisible',
                focused: '',
              }}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-v11-border">
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-1.5 rounded-lg text-xs font-medium border border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              className={cn(
                'px-4 py-1.5 rounded-lg text-xs font-medium transition-colors',
                canApply
                  ? 'bg-blue-500 text-white hover:bg-blue-600 border border-blue-500'
                  : 'bg-v11-surface-hover text-v11-text-muted border border-v11-border cursor-not-allowed',
              )}
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { CustomPeriodPopover }
export type { CustomPeriodPopoverProps }
